import React, { useContext, useEffect, useState } from "react";
import { fetchMaterialSizes } from "../../services/MaterialAPI";
import IDDropdown from "../generic/IDDropdown";
import { UserContext } from "../../context/UserContext";
import { Messages } from "../../constants/Messages";

interface SizeOption {
    id: number; // Unique ID for the size
    label: string; // Display name for the size
}

interface SizeDropdownProps {
    selectedSize: number | null; // Selected size ID (number | null)
    onSelectSize: (size: number | null) => void; // Callback with the selected size ID (number | null)
    refreshKey?: number; // Optional key to trigger re-fetching of sizes
    required?: boolean; // Whether the dropdown is required
}

const SizeDropdown: React.FC<SizeDropdownProps> = ({ selectedSize, onSelectSize, refreshKey, required = true }) => {
    const [ sizeOptions, setSizeOptions ] = useState<{ label: string; value: number }[]>([]);
    const [ loading, setLoading ] = useState<boolean>(true);
    const [ error, setError ] = useState<string | null>(null);
    const { isLoggedIn } = useContext(UserContext);


    useEffect(() => {
        if (isLoggedIn) {
            setError(null);
        }

        const loadSizes = async () => {
            try {
                setLoading(true);
                const sizes = await fetchMaterialSizes(); // Assume fetchMaterialSizes returns an array of size objects
                const formattedSizes = sizes.map((size: { id: number; size: string }) => ({
                    value: size.id,
                    label: size.size,
                }));
                setSizeOptions(formattedSizes);
            } catch (error) {
                setError(Messages.ERROR_LOAD_SIZES);
            } finally {
                setLoading(false);
            }
        };

        loadSizes();
    }, [ refreshKey, isLoggedIn ]); // Trigger re-fetching when refreshKey changes

    const handleChange = (value: number | null) => {
        onSelectSize(value); // Pass the selected size (number or null) to onSelectSize
    };

    return (
        <div>
            <IDDropdown
                label="Material Size"
                options={sizeOptions}
                selectedValue={selectedSize}
                onChange={handleChange}
                loading={loading}
                placeholder="Select a size"
                required={required}
            />
            {error && <p style={{ color: "red" }}>{error}</p>}
        </div>
    );
};

export default SizeDropdown;
